/**
 * Panel 3 — Failure visibility: "FlightAware has been throwing retryable for an
 * hour" as a glance. Per-connector cards, worst-first, stacked mini-bars by the
 * closed five-category taxonomy (never a pie). Breaker controls are ABSENT by
 * audit, so the card reserves their layout slot and renders no dead toggle.
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../theme';
import { FilterChip, SearchInput } from '../../components';
import { fetchOpsFailures, FailureCard } from '../../api/ops';
import { PanelFrame, Mono, EmptyState, Loading, ErrorNote, age } from './OpsScreen';

const WINDOWS = ['24h', '7d', '30d'] as const;
type Window = (typeof WINDOWS)[number];

// Category → tone, per the brief: retryable amber, conflict/not_found neutral,
// unauthorized/permanent red.
const CATEGORY_TONE: Record<string, string> = {
  retryable: colors.warn,
  conflict: colors.neutral,
  not_found: colors.neutral,
  unauthorized: colors.danger,
  permanent: colors.danger,
};
const ORDER = ['retryable', 'conflict', 'not_found', 'unauthorized', 'permanent'];

export default function FailuresPanel() {
  const [cards, setCards] = React.useState<FailureCard[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [window, setWindow] = React.useState<Window>('24h');
  const [org, setOrg] = React.useState('');

  React.useEffect(() => {
    let live = true;
    setCards(null);
    setError(null);
    fetchOpsFailures(window, org.trim() || undefined)
      .then((r) => live && setCards(r.connectors))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [window, org]);

  return (
    <PanelFrame
      title="Failure visibility"
      subtitle="Per-connector health over the selected window, worst-first. Alerts arrive via the ops channel."
      toolbar={
        <>
          {WINDOWS.map((w) => (
            <FilterChip key={w} label={w} active={window === w} onPress={() => setWindow(w)} />
          ))}
          <View style={{ width: 300 }}>
            <SearchInput value={org} onChange={setOrg} placeholder="Scope to one org (optional)…" mono />
          </View>
        </>
      }
    >
      {error ? <ErrorNote text={`Could not load failure data: ${error}`} /> : null}
      {cards === null && !error ? <Loading /> : null}
      {cards !== null && cards.length === 0 ? (
        <EmptyState text="No executions in this window. Alerts arrive via the ops channel." />
      ) : null}
      {cards !== null && cards.length > 0 ? (
        <View style={styles.grid}>
          {cards.map((c) => (
            <Card key={c.connector} card={c} />
          ))}
        </View>
      ) : null}
    </PanelFrame>
  );
}

function Card({ card }: { card: FailureCard }) {
  const peak = Math.max(
    1,
    ...card.buckets.map((b) =>
      Object.entries(b).reduce((sum, [k, v]) => (k === 't' ? sum : sum + (typeof v === 'number' ? v : 0)), 0),
    ),
  );
  const failPct = Math.round(card.fail_rate * 100);
  const healthy = card.failed === 0;

  return (
    <View style={[styles.card, !healthy && card.fail_rate >= 0.25 ? styles.cardSick : null]}>
      <View style={styles.cardHead}>
        <View style={{ flex: 1 }}>
          <Mono>{card.connector}</Mono>
          <Text style={styles.cardName}>{card.connector_name}</Text>
        </View>
        <View style={styles.headline}>
          <Text style={[styles.failPct, healthy ? { color: colors.success } : { color: colors.danger }] as any}>
            {failPct}%
          </Text>
          <Text style={styles.headlineLabel}>failed</Text>
        </View>
      </View>

      {/* Stacked mini-bars: composition over time. Never a pie. Bars keep a
          bucket-sized width so a sparse window reads as a time series, not as
          one full-width progress bar. */}
      <View style={styles.chart}>
        {card.buckets.map((b) => {
          const total = Object.entries(b).reduce(
            (sum, [k, v]) => (k === 't' ? sum : sum + (typeof v === 'number' ? v : 0)),
            0,
          );
          return (
            <View key={String(b.t)} style={styles.barSlot} {...({ title: `${String(b.t)} · ${total} executions` } as object)}>
              <View style={[styles.bar, { height: `${Math.max(4, (total / peak) * 100)}%` } as any]}>
                {ORDER.filter((cat) => typeof b[cat] === 'number' && (b[cat] as number) > 0).map((cat) => (
                  <View
                    key={cat}
                    style={{
                      flex: b[cat] as number,
                      backgroundColor: CATEGORY_TONE[cat],
                    }}
                  />
                ))}
                {typeof b.ok === 'number' && b.ok > 0 ? (
                  <View style={{ flex: b.ok as number, backgroundColor: colors.successLine }} />
                ) : null}
              </View>
            </View>
          );
        })}
      </View>

      <View style={styles.legend}>
        <Text style={styles.counts}>
          <Text style={styles.countsMono}>{card.total}</Text> executions ·{' '}
          <Text style={styles.countsMono}>{card.failed}</Text> failed
        </Text>
      </View>

      {Object.keys(card.categories).length > 0 ? (
        <View style={styles.cats}>
          {ORDER.filter((cat) => card.categories[cat]).map((cat) => (
            <View key={cat} style={styles.catRow}>
              <View style={[styles.swatch, { backgroundColor: CATEGORY_TONE[cat] } as any]} />
              <Text style={styles.catLabel}>{cat}</Text>
              <Text style={styles.catCount}>{card.categories[cat]}</Text>
            </View>
          ))}
        </View>
      ) : null}

      {card.last_error ? (
        <Text style={styles.lastError} {...({ title: card.last_error.at } as object)}>
          Last error {age(card.last_error.at)} · {card.last_error.category ?? 'unknown'}
          {card.last_error.operation ? ` · ${card.last_error.operation}` : ''}
        </Text>
      ) : (
        <Text style={styles.noError}>No failures in this window.</Text>
      )}

      {/* Reserved slot: circuit breakers are ABSENT by audit. Layout only —
          rendering a disabled toggle would be a dead control. */}
      <View style={styles.breakerSlot} />
    </View>
  );
}

const styles = StyleSheet.create({
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  card: {
    width: 340,
    padding: 14,
    gap: 10,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    backgroundColor: colors.card,
  },
  cardSick: { borderColor: colors.dangerLine },
  cardHead: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  cardName: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink4, marginTop: 2 },
  headline: { alignItems: 'flex-end' },
  failPct: { fontFamily: fontFamily.mono, fontSize: fontSize.xl, fontWeight: fontWeight.semibold },
  headlineLabel: { fontFamily: fontFamily.sans, fontSize: 10, color: colors.ink4 },

  chart: { flexDirection: 'row', alignItems: 'flex-end', gap: 2, height: 56 },
  // maxWidth caps a lone bucket at bar width instead of stretching the card.
  barSlot: { flex: 1, maxWidth: 26, height: '100%', justifyContent: 'flex-end' } as any,
  bar: { width: '100%', flexDirection: 'column-reverse', borderRadius: 2, overflow: 'hidden', minHeight: 2 } as any,

  legend: { flexDirection: 'row', alignItems: 'center' },
  counts: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink3 },
  countsMono: { fontFamily: fontFamily.mono, color: colors.ink2 },

  cats: { gap: 4 },
  catRow: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  swatch: { width: 8, height: 8, borderRadius: 2 },
  catLabel: { flex: 1, fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  catCount: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink2 },

  lastError: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.danger },
  noError: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink4 },
  breakerSlot: { height: 0 },
});
